import React from 'react';
import { API_BASE } from '../../config';
import FitnessProgramManagement from '../FitnessProgramManagement';
import ExerciseLibraryBrowser from '../ExerciseLibraryBrowser';
import SportsHobbiesManager from '../SportsHobbiesManager';
import TrainerProviderForm from './TrainerProviderForm';
import SendTrainerReportModal from './SendTrainerReportModal';
import ManageTrainerReportsModal from './ManageTrainerReportsModal';
import TrainerReportProgressModal from './TrainerReportProgressModal';
import { useConfirm } from '../ConfirmProvider';

function ExerciseManagement({ fetchWithAuth, fetchWithAuthFormData, setShowAIAssistant, setAiAgentInitialTool, token, setActiveTab }) {
  const confirm = useConfirm();
  const [activeSubTab, setActiveSubTab] = React.useState('programs'); // 'programs', 'library', 'trainers'
  const [programRefreshKey, setProgramRefreshKey] = React.useState(0);
  // Trainer state
  const [trainers, setTrainers] = React.useState([]);
  const [showTrainerForm, setShowTrainerForm] = React.useState(false);
  const [editingTrainer, setEditingTrainer] = React.useState(null);
  const [showSendTrainerReportModal, setShowSendTrainerReportModal] = React.useState(false);
  const [showManageTrainerReportsModal, setShowManageTrainerReportsModal] = React.useState(false);
  const [trainerReports, setTrainerReports] = React.useState([]);
  const [showTrainerProgressModal, setShowTrainerProgressModal] = React.useState(false);
  const [trainerReportProgress, setTrainerReportProgress] = React.useState(0);
  const [trainerReportComplete, setTrainerReportComplete] = React.useState(false);
  const [trainerResponses, setTrainerResponses] = React.useState([]);

  React.useEffect(() => {
    loadTrainers();
    loadTrainerReports();
  }, []);

  const handleOpenAIAgent = (toolId) => {
    if (setAiAgentInitialTool) setAiAgentInitialTool(toolId);
    if (setShowAIAssistant) setShowAIAssistant(true);
  };

  // Trainer functions
  const loadTrainers = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/trainers`);
      if (response.ok) {
        const data = await response.json();
        setTrainers(data);
      }
    } catch (error) {
      console.error('Failed to load trainers:', error);
    }
  };

  const loadTrainerReports = async () => {
    try {
      const [reportsRes, responsesRes] = await Promise.all([
        fetchWithAuth(`${API_BASE}/api/trainer-reports/list`),
        fetchWithAuth(`${API_BASE}/api/trainer-reports/responses`)
      ]);

      if (reportsRes.ok) {
        const data = await reportsRes.json();
        setTrainerReports(data);
      }
      if (responsesRes.ok) {
        const data = await responsesRes.json();
        setTrainerResponses(data);
      }
    } catch (error) {
      console.error('Failed to load trainer reports:', error);
    }
  };

  const handleEditTrainer = (trainer) => {
    setEditingTrainer(trainer);
    setShowTrainerForm(true);
  };

  const handleDeleteTrainer = async (trainerId) => {
    if (await confirm({
      title: 'Delete this trainer?',
      message: 'They will be removed from your providers, and any reports you shared with them will stop being updated.',
      confirmLabel: 'Delete Trainer',
      destructive: true,
    })) {
      try {
        const response = await fetchWithAuth(`${API_BASE}/api/trainers/${trainerId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          loadTrainers();
        }
      } catch (error) {
        console.error('Failed to delete trainer:', error);
      }
    }
  };

  const closeTrainerForm = () => {
    setShowTrainerForm(false);
    setEditingTrainer(null);
  };

  const handleSendTrainerReport = async (selectedData, selectedTrainerIds) => {
    try {
      // Show progress modal
      setShowSendTrainerReportModal(false);
      setShowTrainerProgressModal(true);
      setTrainerReportProgress(0);
      setTrainerReportComplete(false);

      // Simulate initial progress
      setTrainerReportProgress(10);

      const response = await fetchWithAuth(`${API_BASE}/api/trainer-reports/generate`, {
        method: 'POST',
        body: JSON.stringify({
          data_elements: selectedData,
          trainer_ids: selectedTrainerIds
        })
      });

      if (response.ok) {
        const result = await response.json();
        // Start polling for progress updates
        pollTrainerReportProgress(result.report_id);
      } else {
        alert('Failed to start report generation');
        setShowTrainerProgressModal(false);
      }
    } catch (error) {
      console.error('Failed to send trainer report:', error);
      alert('Error sending report');
      setShowTrainerProgressModal(false);
    }
  };

  const pollTrainerReportProgress = async (reportId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE}/api/trainer-reports/status/${reportId}`);

        if (response.ok) {
          const status = await response.json();
          setTrainerReportProgress(status.progress_percentage);

          if (status.generation_status === 'completed' || status.generation_status === 'sent') {
            setTrainerReportComplete(true);
            clearInterval(pollInterval);
            loadTrainerReports();
          } else if (status.generation_status === 'failed') {
            alert('Report generation failed. Please try again.');
            setShowTrainerProgressModal(false);
            clearInterval(pollInterval);
          }
        }
      } catch (error) {
        console.error('Error polling trainer report status:', error);
        clearInterval(pollInterval);
      }
    }, 2000); // Poll every 2 seconds
  };

return (
    <div className="page-layout">
      <div className="page-sticky-nav">
        <div className="page-header-row">
          <h2 className="page-section-title">Exercise & Fitness</h2>
          <button className="page-exit-btn" onClick={() => setActiveTab('dashboard')}>← Dashboard</button>
        </div>
        <div className="page-nav-bar">
          {/* Tab Navigation */}
          <div className="tab-bar">
            <button
              onClick={() => setActiveSubTab('programs')}
              className={`tab-item ${activeSubTab === 'programs' ? 'tab-item--active' : ''}`}
            >
              Fitness Programs
            </button>
            <button
              onClick={() => setActiveSubTab('library')}
              className={`tab-item ${activeSubTab === 'library' ? 'tab-item--active' : ''}`}
            >
              Exercise Library
            </button>
            <button
              onClick={() => setActiveSubTab('trainers')}
              className={`tab-item ${activeSubTab === 'trainers' ? 'tab-item--active' : ''}`}
            >
              🏃 Trainers / Providers
            </button>
            <button
              onClick={() => setActiveSubTab('sports')}
              className={`tab-item ${activeSubTab === 'sports' ? 'tab-item--active' : ''}`}
            >
              Sports & Hobbies
            </button>
          </div>
        </div>
      </div>

      <div className="page-scroll-content">
      {/* ========== FITNESS PROGRAMS TAB ========== */}
      {activeSubTab === 'programs' && (
        <FitnessProgramManagement
          fetchWithAuth={fetchWithAuth}
          fetchWithAuthFormData={fetchWithAuthFormData}
          onOpenAIAgent={handleOpenAIAgent}
          refreshKey={programRefreshKey}
          token={token}
        />
      )}

      {/* ========== EXERCISE LIBRARY TAB ========== */}
      {activeSubTab === 'library' && (
        <ExerciseLibraryBrowser
          fetchWithAuth={fetchWithAuth}
        />
      )}

      {/* ========== TRAINERS / PROVIDERS TAB ========== */}
      {activeSubTab === 'trainers' && (
        <div className="card">
          <h3 className="card-title">Trainers / Providers</h3>

          {/* Button Row */}
          <div className="exf-buttonRow">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowTrainerForm(true)}
            >
              Add Trainer
            </button>

            {/* Send Reports relocated to Analytics & Reporting → Reports tab */}
          </div>

          <hr className="divider" />

          {trainers.length === 0 ? (
            <p>No trainers recorded. Add your first trainer!</p>
          ) : (
            <div>
              {trainers.map(trainer => (
                <div key={trainer.id} className="objective-item">
                  <div className="objective-content">
                    <h4>{trainer.trainer_name}</h4>
                    <p>{trainer.trainer_type}</p>
                    {trainer.trainer_certification && (
                      <p className="text-helper">
                        Certification: {trainer.trainer_certification}
                      </p>
                    )}
                  </div>
                  <div className="objective-actions">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleEditTrainer(trainer)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteTrainer(trainer.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========== SPORTS & HOBBIES TAB (from Profile) ========== */}
      {activeSubTab === 'sports' && (
        <SportsHobbiesManager token={token} fetchWithAuth={fetchWithAuth} />
      )}

      {/* Trainer Form Modal */}
      {showTrainerForm && (
        <TrainerProviderForm
          onClose={closeTrainerForm}
          onSave={() => {
            loadTrainers();
            closeTrainerForm();
          }}
          fetchWithAuth={fetchWithAuth}
          editingTrainer={editingTrainer}
        />
      )}

      {/* Send Trainer Report Modal */}
      {showSendTrainerReportModal && (
        <SendTrainerReportModal
          trainers={trainers}
          onClose={() => setShowSendTrainerReportModal(false)}
          onSendReport={handleSendTrainerReport}
        />
      )}

      {/* Trainer Report Progress Modal */}
      {showTrainerProgressModal && (
        <TrainerReportProgressModal
          progress={trainerReportProgress}
          isComplete={trainerReportComplete}
          onDismiss={() => {
            setShowTrainerProgressModal(false);
            setTrainerReportProgress(0);
            setTrainerReportComplete(false);
          }}
        />
      )}

      {/* Manage Trainer Reports Modal */}
      {showManageTrainerReportsModal && (
        <ManageTrainerReportsModal
          reports={trainerReports}
          responses={trainerResponses}
          onClose={() => setShowManageTrainerReportsModal(false)}
          fetchWithAuth={fetchWithAuth}
          onRefresh={loadTrainerReports}
        />
      )}
      </div>
    </div>
  );
}

export default ExerciseManagement;
